/**
 * Passkey self-service — *"I do not see a way for me to setup a passkey"*, answered.
 *
 * ⭐ PORTED FROM `cloud/dp/shell/src/home/pages/settings/PasskeySettingsPage.tsx` — the owner asked
 * for the DP header, and passkeys are what the DP header's Settings item leads to. ⛔ Read, never
 * imported: `cloud/dp/shell` is a different package, and every endpoint was re-derived from
 * `cp-auth-api` (see `../api/passkeys.api.ts`'s header for the route table).
 *
 * ⛔ **THREE THINGS FROM THE DP ORIGINAL ARE DELIBERATELY NOT PORTED**, because each is a defect
 * rather than a design:
 *   1. `prompt()` for the device name (`PasskeySettingsPage.tsx:41`) — a blocking browser dialog with
 *      no label, no validation and a default of `'My passkey'` when dismissed, which is how you get a
 *      list of identically-named credentials nobody can tell apart. Here it is a real labelled field
 *      and the name is REQUIRED.
 *   2. `confirm()` for removal (`:55`) — one keystroke (Enter) completes it. Platform-5 forbids
 *      exactly that; see below.
 *   3. `as unknown as` on the options cast (`:42-44`) — banned repo-wide, and unnecessary once the
 *      client type aliases the library's own (`passkeys.api.ts`).
 *
 * ## ⛔ WHY THIS IS A DIALOG AND NOT A SCREEN
 *
 * The DP equivalent is a route (`/settings/passkeys`) under a settings layout. A route here would
 * mean an entry in `shell/manifest.ts` — *"the only door"* for this console's routing, and owned by
 * another workstream this run. A dialog needs no route, no manifest entry and no rail slot, and it
 * makes the surface reachable from every screen rather than one, which for a control an operator
 * touches once or twice ever is the better trade anyway.
 *
 * ## ⚠️ THIS IS THE PACKAGE'S FIRST *TRUE* MODAL, AND `panes/dialog-panel.tsx` IS NOT ONE
 *
 * That file is emphatic that its nine `role="dialog"` panels are **inline, in-flow and non-modal**,
 * and that `aria-modal="true"` is gone from them and *"is not coming back"* — they had no overlay, no
 * focus trap and nothing inert, so the modality claim was false in both directions. Its closing
 * sentence names the price of a real one: *"a true modal needs an overlay, a focus trap and `inert`
 * on the rest of the document."*
 *
 * ⇒ This one pays it **in full**, so the claim is honest in every direction: a `fixed inset-0`
 * overlay, focus moved into the card on mount, Escape to dismiss, a **Tab trap**
 * ({@link trapTabTarget}) that cannot be left by keyboard, a **portal** out to `<body>`, and
 * **`inert` on every other body child** ({@link inertSiblings}) so the page behind is unreachable by
 * pointer, by keyboard and by AT tree-walking alike. ⛔ It does not re-implement
 * `panes/dialog-panel.tsx`'s helpers — `focusPanel` and `handleDialogKey` are imported from it, so
 * "focus the container" and "Escape means cancel" keep one definition in this package.
 *
 * ⚠️ **THE PORTAL IS WHAT MAKES `inert` POSSIBLE, AND IT IS NOT COSMETIC.** Rendered in place, this
 * dialog's siblings are the *header's* children — there is no element that means "the rest of the
 * app" to mark. Portaled to `<body>`, the thing to inert is "every body child except our own host",
 * which needs no knowledge of what `main.tsx` mounts into.
 *
 * ⛔ **`inert` has ONE non-obvious consequence and it is documented at {@link requestClose}: you
 * cannot focus an element inside an inert subtree**, so the release must happen BEFORE `onClose`
 * runs, not in the effect cleanup. Read that comment before touching any close path.
 *
 * ⚠️ **This is now the precedent every later modal on this surface will copy.** If you add one, copy
 * all five pieces — portal, inert-with-ordered-release, focus in, Tab trap, focus out from an event —
 * or copy none and stay non-modal like `dialog-panel.tsx`. Half of them is the false-claim state that
 * file exists to warn about.
 *
 * ## ⛔ WHAT IS ACTUALLY VERIFIED, AND WHAT A REAL BROWSER STILL HAS TO CONFIRM
 *
 * ⛔ **Nothing below has been run in a browser.** Every claim in this file is from `vitest` +
 * happy-dom, which has no layout engine, no hit-testing, no compositor and no assistive technology.
 * Read this list before adding "verified" to anything here.
 *
 * ✅ **Verified in happy-dom, and mutation-proved** (delete the mechanism ⇒ a named test reddens):
 *   · `inert` is applied to every other body child while open and released on unmount;
 *   · `inert` really blocks `.focus()` here — happy-dom implements that much, established with a
 *     throwaway probe BEFORE any assertion was written, precisely so the behavioural test could not
 *     pass against an environment that ignores the attribute;
 *   · the release happens strictly BEFORE `onClose`, asserted as the sequence `was-inert → released`
 *     (the second half alone is vacuous — it holds trivially when `inert` was never applied);
 *   · Tab wraps at both boundaries and is left alone in the interior;
 *   · focus lands on the card on mount and returns to the opener's trigger on close.
 *
 * ⚠️ **NOT verified here, and NOT verifiable here** — a real browser (or an axe/Playwright pass) is
 * the only thing that can answer these, and each has a plausible failure mode:
 *   1. **`inert` blocking POINTER input.** happy-dom does no hit-testing, so the focus assertion says
 *      nothing about clicks. If a browser disagreed, the background would be clickable through the
 *      scrim while `aria-modal` claimed otherwise.
 *   2. **AT actually pruning the background.** There is no screen reader in the suite; `aria-modal`
 *      plus `inert` is the correct request, and whether NVDA/VoiceOver honour it is unobserved.
 *   3. **That the dialog genuinely PAINTS above the chrome.** No compositor here, so stacking is
 *      entirely untested, and a modal rendering *behind* the header is a defect no jsdom-family test
 *      can see. ⚠️ But the reasoning is worth stating rather than left as "should", because it points
 *      the opposite way from the obvious worry:
 *
 *      **The portal REDUCES this risk rather than raising it.** Measured across `src/**` — the only
 *      z-indexes in the entire package are `z-40` (the user menu) and `z-50` (this overlay and
 *      `SectionLayout`'s skip link); there is **no `transform`, `will-change` or `filter` anywhere in
 *      the shell or `index.css`**, so nothing creates a containing block. Inline, this dialog sat
 *      inside `<header>` and depended on that staying true — `position: fixed` is defeated by an
 *      ancestor `transform`, which is a one-line change away in any chrome file. Portaled, the host is
 *      a direct `<body>` child appended AFTER `#root`, so it wins on z-index and wins the tie on DOM
 *      order too.
 *
 *      ⛔ What would still break it, so the next reader knows what to look for: giving `#root` a
 *      `z-index` above 50, or appending another body child after our host with a higher one. Neither
 *      exists today. ⚠️ And one deliberate consequence: a focused skip link (`focus:z-50`, inside
 *      `#root`) paints BEHIND this scrim — correct, since the page is inert while the dialog is open.
 *   4. **`max-h-[calc(100vh-2rem)]`, the body region's `min-h-0 flex-1 overflow-y-auto` and the
 *      list's `max-h-64` scroll boundary.** No viewport, so the overflow behaviour at small heights
 *      is unobserved. A test pins the classes and which controls sit inside the body region.
 *   5. **`prefers-reduced-motion`.** The skeleton pulse and spinner carry `motion-reduce:animate-none`
 *      and there is no other motion, but no test exercises the media query.
 *   6. **The WebAuthn ceremony itself.** `createCredential` is a seam in every test; `startRegistration`
 *      has never run against a real authenticator on this console.
 *
 * ## ⛔ PLATFORM-5: REMOVAL REQUIRES TYPING, AND ENTER CANNOT COMPLETE IT
 *
 * *"Every destructive action must require TYPING a value to confirm, and must NOT be completable with
 * Enter."* Removing a passkey is squarely destructive-class: it deletes a credential that can
 * authenticate as the operator, and on an account that relies on it, it is a self-lockout.
 *
 * Two independent mechanisms enforce the Enter half, because either alone is a single point of
 * failure:
 *   1. ⛔ There is **no `<form>` anywhere in this component**, so there is no implicit submission to
 *      trigger in the first place.
 *   2. ⛔ The confirm field additionally `preventDefault()`s Enter ({@link isForbiddenEnter}), so a
 *      later refactor that wraps this in a form cannot silently re-open the hole.
 *
 * ⚠️ And nothing else here is Enter-completable either — the name field does not submit on Enter — so
 * the one place Enter is *forbidden* is not an inconsistency the operator has to learn.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { KeyRound, Loader2, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { displayMessage } from '../platform/errors';
import { useApiResource } from '../panes/async-state';
import { CellAbsence, EmptyState, ErrorState } from './absence';
import { formatTimestamp } from '../panes/format';
import { focusPanel, handleDialogKey } from '../panes/dialog-panel';
import { Button } from './Button';
import { RecoveryCodesPanel } from './RecoveryCodesPanel';
import {
  PASSKEYS_PATH,
  createPasskeyCredential,
  deleteMyPasskey,
  listMyPasskeys,
  myPasskeyRegistrationOptions,
  registerMyPasskey,
  type OperatorPasskey,
  type PasskeyRegistrationOptions,
  type RegistrationResponseJSON,
} from '../api/passkeys.api';

const TITLE_ID = 'passkey-dialog-title';

/**
 * What the operator must type to confirm removal.
 *
 * ⛔ ONE mechanism, not two: they always type **the string the dialog shows them**. The fallback
 * exists because `displayName` can legitimately be `''` — `passkey.controller.ts:104` projects
 * `row.deviceName ?? ''`, and every passkey enrolled before this dialog existed may be unnamed (the
 * DP page defaulted to a dismissed `prompt()`, and cp-auth-api's `deviceName` column is nullable).
 * Requiring an empty string as the token would make the gate unpassable; treating an empty token as
 * a no-op would make the gate ABSENT while the control still looked guarded, which is worse.
 */
export function confirmTokenFor(displayName: string): string {
  return displayName.trim() === '' ? 'REMOVE' : displayName;
}

/**
 * How an unnamed passkey reads on screen.
 *
 * ⛔ *"Unnamed passkey"*, never a blank line and never a fabricated *"Passkey"*. A blank row reads as
 * a rendering bug; a fabricated name is indistinguishable from one the operator chose — which matters
 * precisely here, because the name is what they identify the credential BY when deciding which to
 * delete. (site-admin-api applies exactly such a `?? 'Passkey'` default on its admin-facing read,
 * `operator-admin.service.ts:141`. ⛔ Not copied.)
 */
export function passkeyLabel(displayName: string): string {
  return displayName.trim() === '' ? 'Unnamed passkey' : displayName;
}

/**
 * ⛔ EXACT, case-sensitive, un-trimmed. A gate that accepts `.trim().toLowerCase()` accepts a
 * mis-typed value, which is the whole thing Platform-5 defends against — the operator is meant to
 * have READ the name, and a lenient compare lets a half-read one through.
 */
export function confirmMatches(typed: string, token: string): boolean {
  return typed === token;
}

/** Enter must never complete a destructive action (Platform-5). Exported so both arms are asserted. */
export function isForbiddenEnter(key: string): boolean {
  return key === 'Enter';
}

/** Whether "Add passkey" can be pressed. */
export function canAdd(deviceName: string, busy: boolean): boolean {
  return deviceName.trim() !== '' && !busy;
}

/**
 * Why "Add passkey" is unavailable, or `null` when it is available.
 *
 * ⛔ The button is ALWAYS RENDERED and disabled-with-a-reason, never conditionally hidden. A control
 * that vanishes leaves the operator with nothing to read and no idea what to do next; a disabled one
 * carrying its own `title` answers the question at the point they ask it.
 */
export function addBlockedReason(deviceName: string, busy: boolean): string | null {
  if (busy) return 'Waiting for your authenticator…';
  if (deviceName.trim() === '') return 'Name the device first';
  return null;
}

/**
 * Every focusable element inside the card, in document order.
 *
 * ⚠️ The selector is deliberately NARROW — this component renders only buttons and text inputs —
 * rather than the sprawling "anything that could conceivably be focusable" query. A trap is correct
 * only if its idea of "first" and "last" matches the browser's, and a broad selector that also
 * matched `[tabindex="-1"]` would pick up the card itself and make the real first stop unreachable.
 */
export function focusablesIn(card: HTMLElement | null): readonly HTMLElement[] {
  if (card === null) return [];
  return [...card.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])')];
}

/**
 * Make every direct child of `body` inert except `except`, and answer the ones actually changed.
 *
 * ⛔ **BODY CHILDREN, not `#root` by id.** Two reasons. It needs no knowledge of what `main.tsx`
 * mounts into, so it cannot rot when that changes; and it is correct in the test environment too,
 * where RTL mounts into its own generated `<div>` rather than into the app's root — an
 * id-coupled version would inert nothing under test and there would be no way to assert any of this.
 *
 * ⛔ **An element that ALREADY carries `inert` is skipped and NOT returned**, so it is not released
 * on the way out. That is deliberate: whoever set it owns it. Without the skip, two stacked modals
 * would have the inner one's teardown un-inert the page while the outer one is still open — the
 * classic dialog-stack bug, and it would look like the outer dialog had spontaneously stopped
 * trapping.
 *
 * ⚠️ `setAttribute`, not the `inert` IDL property. The attribute is what the HTML spec defines and
 * what makes the state assertable with `toHaveAttribute`; the property is a convenience that not
 * every environment reflects.
 */
export function inertSiblings(body: HTMLElement, except: Element | null): readonly Element[] {
  const changed: Element[] = [];
  /*
   * ⚠️ Iterating the LIVE `HTMLCollection` directly, no defensive snapshot — and that is safe here for
   * a specific reason rather than by luck: this loop only sets an ATTRIBUTE, so it never adds or
   * removes a body child and the collection cannot shift under it. ⛔ If a future edit makes this loop
   * append or remove anything, re-introduce the `[...body.children]` copy; a live collection mutated
   * mid-iteration silently skips elements.
   */
  for (const child of body.children) {
    if (child === except) continue;
    if (child.hasAttribute('inert')) continue;
    child.setAttribute('inert', '');
    changed.push(child);
  }
  return changed;
}

/** Release exactly what {@link inertSiblings} took. Idempotent, so it is safe to call twice. */
export function releaseInert(elements: readonly Element[]): void {
  for (const element of elements) element.removeAttribute('inert');
}

/**
 * Where a Tab press must land to keep focus inside the card, or `null` to let the browser handle it.
 *
 * ⛔ Returns the TARGET rather than performing the focus, so all four arms — nothing focusable,
 * wrap backwards, wrap forwards, and an ordinary interior press — are assertable without a rendered
 * dialog.
 */
export function trapTabTarget(
  focusables: readonly HTMLElement[],
  active: Element | null,
  shiftKey: boolean,
): HTMLElement | null {
  if (focusables.length === 0) return null;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (shiftKey && active === first) return last;
  if (!shiftKey && active === last) return first;
  return null;
}

export interface PasskeyDialogProps {
  /**
   * Dismiss the dialog.
   *
   * ⛔ **THE CALLER OWNS FOCUS RESTORATION; THIS COMPONENT DELIBERATELY DOES NOT.**
   * `panes/dialog-panel.tsx` restores to `document.activeElement`-at-mount, which is the wrong answer
   * here: this dialog opens FROM a menu item that unmounts in the same commit, so the captured opener
   * is a detached node and focusing it is a silent no-op that dumps the operator on `<body>`.
   * `UserMenu` returns focus to the menu TRIGGER from this callback — an event, never an effect, so
   * it cannot fire on mount and steal focus (a defect this codebase has shipped before).
   */
  readonly onClose: () => void;
  /**
   * The WebAuthn ceremony. Defaults to the real `startRegistration` wrapper.
   *
   * ⚠️ An injectable seam ONLY because happy-dom implements no WebAuthn and the repo bans
   * `vi.fn()`/`vi.mock()` — the same shape and the same reason as `useAuth`'s `navigateExternal`.
   * ⛔ Not a place to pass a different ceremony in production: the default is the only
   * rule-compliant call (`createPasskeyCredential`, which is `startRegistration({ optionsJSON })`
   * and nothing else).
   */
  readonly createCredential?: (
    options: PasskeyRegistrationOptions,
  ) => Promise<RegistrationResponseJSON>;
}

export function PasskeyDialog({
  onClose,
  createCredential = createPasskeyCredential,
}: PasskeyDialogProps): React.JSX.Element {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const passkeys = useApiResource<readonly OperatorPasskey[]>(PASSKEYS_PATH, listMyPasskeys);

  const [deviceName, setDeviceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The id whose removal is armed, or `null`. ⛔ One at a time — arming a second disarms the first. */
  const [armedId, setArmedId] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  /**
   * `true` from the confirm of a replace in `RecoveryCodesPanel` until the operator closed the ten
   * new recovery codes, or until the replace failed (kernel apps stage 5, WS-31b, WS-31b-R11). The
   * server never sends the codes again, so Done, Escape and the backdrop hold while the POST is out
   * and while the codes show: a close in either window would throw away codes nobody saved.
   */
  const [holding, setHolding] = useState(false);

  /**
   * The portal container.
   *
   * ⛔ Created in a `useState` INITIALISER, not in an effect, and that is load-bearing rather than
   * stylistic. An effect-created host is `null` on the first render, so the dialog renders nothing on
   * the first commit — which means `cardRef` is `null` when the mount-focus effect below runs, and
   * focus never lands on the card at all. The initialiser runs once, during the first render, so the
   * host exists before anything else needs it. (Creating a DETACHED element is not a side effect;
   * appending it is, and that happens in the effect.)
   */
  const [host] = useState(() => document.createElement('div'));
  const releasedRef = useRef<readonly Element[]>([]);

  /*
   * ⭐ THE MODALITY, MADE TRUE. `aria-modal="true"` asks assistive technology to prune everything
   * outside this dialog; `inert` is what makes that request honest for pointer and keyboard as well.
   * Without it the claim is false in one direction — which `panes/dialog-panel.tsx` argues at length
   * is worse for an AT user than omitting the attribute, because they act on the claim.
   *
   * ⛔ DECLARED BEFORE THE FOCUS EFFECT, because effects run in declaration order and focusing an
   * element in a DETACHED tree does nothing. The host must be in the document first.
   */
  useEffect(() => {
    host.setAttribute('data-passkey-portal', '');
    document.body.append(host);
    releasedRef.current = inertSiblings(document.body, host);
    return () => {
      // The backstop, for an unmount that did not go through `requestClose` — a parent unmounting,
      // or a route change. `releaseInert` is idempotent, so the double-release is harmless.
      releaseInert(releasedRef.current);
      host.remove();
    };
  }, [host]);

  /*
   * Move focus into the card on mount.
   *
   * ⛔ THE CARD, not the first control. Focusing the name field would announce a text box before the
   * sentence explaining what the dialog is; focusing a Remove button would put initial focus on a
   * destructive choice. Same reasoning, and the same helper, as `panes/dialog-panel.tsx`.
   */
  useEffect(() => {
    focusPanel(cardRef.current);
  }, []);

  /**
   * ⛔⛔ **RELEASE `inert` BEFORE CALLING `onClose`, NEVER ONLY IN THE CLEANUP.** This is the one
   * non-obvious consequence of adding `inert`, and getting it wrong breaks a property that is already
   * asserted.
   *
   * `UserMenu.closePasskeys` returns focus to the menu trigger synchronously, from the event — which
   * is what it must do (an effect would fire on mount and steal focus). But the trigger lives in the
   * header, i.e. inside a body child this dialog just made inert, **and you cannot focus an element
   * inside an inert subtree.** React runs effect cleanup AFTER the event handler, so a
   * cleanup-only release means the focus call happens while the page is still inert: it silently does
   * nothing and the operator is dumped on `<body>`.
   *
   * ⚠️ MEASURED IN happy-dom, not merely reasoned — it implements inert focus-blocking, established by
   * probe before the assertion was written. So this ordering is guarded by tests rather than only by
   * this comment: moving the release into the cleanup reddens the ordering tests here AND the
   * focus-restore test in `user-menu.test.tsx`. ⛔ It has NOT been run in a browser; see this file's
   * "what a real browser still has to confirm" list.
   */
  function requestClose(): void {
    if (holding) return;
    releaseInert(releasedRef.current);
    onClose();
  }

  /** Arming (or disarming) always clears the typed value — a stale one is a half-passed gate. */
  function armRemoval(id: string | null): void {
    setArmedId(id);
    setTyped('');
    setError(null);
  }

  async function handleAdd(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const options = await myPasskeyRegistrationOptions();
      const credential = await createCredential(options);
      await registerMyPasskey(deviceName.trim(), credential);
      setDeviceName('');
      /*
       * ⛔ Re-READ rather than appending the POST's response body: that body is the raw
       * `operator_passkeys` row, public-key bytes and all (`passkey.service.ts:130-139`). The list
       * endpoint is the projected shape. See `passkeys.api.ts`'s `registerMyPasskey`.
       */
      passkeys.reload();
    } catch (cause) {
      setError(displayMessage(cause, 'Could not add the passkey.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(id: string): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await deleteMyPasskey(id);
      armRemoval(null);
      passkeys.reload();
    } catch (cause) {
      setError(displayMessage(cause, 'Could not remove the passkey.'));
    } finally {
      setBusy(false);
    }
  }

  function onCardKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    handleDialogKey(event.key, requestClose);
    if (event.key !== 'Tab') return;
    const target = trapTabTarget(
      focusablesIn(cardRef.current),
      document.activeElement,
      event.shiftKey,
    );
    if (target === null) return;
    event.preventDefault();
    target.focus();
  }

  function row(passkey: OperatorPasskey, isOnly: boolean): React.JSX.Element {
    const armed = armedId === passkey.id;
    const token = confirmTokenFor(passkey.displayName);
    const matched = confirmMatches(typed, token);
    return (
      <li
        key={passkey.id}
        data-testid={`passkey-row-${passkey.id}`}
        className="rounded-lg border border-border-subtle px-3 py-2"
      >
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent"
          >
            <KeyRound className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-primary">
              {passkeyLabel(passkey.displayName)}
            </span>
            <span className="block text-xs text-muted">
              Added {formatTimestamp(passkey.createdAt)}
            </span>
            {/*
              ⭐ **C10 / WS-4 G6 — the "Last used" line, now that a column backs it.** An earlier comment
              here explained that the producer sent `lastUsedAt` hardcoded `null` so this line had no data
              behind it; `cp_auth.operator_passkeys.last_used_at` now exists and the service stamps it in
              the same UPDATE as the replay counter, so that comment was deleted rather than reworded — a
              comment asserting a property the producer denies is worse than no comment.

              ⛔ **`kind="empty"`, NOT `notcollected`.** The producer sent the field and the answer is
              genuinely "no authentication recorded" — `notcollected` would send an operator hunting for a
              collection failure that does not exist. ⚠️ The column was NOT backfilled, so an
              already-enrolled passkey reads `not used yet` until its next login even though it has been
              used before; that is the honest statement of what we hold, and the alternative (a
              `DEFAULT now()`) would have asserted a use that never happened.

              ⛔ ABSOLUTE timestamp via the same `formatTimestamp` as `Added` — a relative "3 days ago"
              beside an absolute date on the line above would make two different clocks share one row.
            */}
            <span className="block text-xs text-muted">
              {passkey.lastUsedAt === null ? (
                <CellAbsence
                  kind="empty"
                  label="not used yet"
                  reason="This passkey has no recorded authentication. Last-use is stamped on each successful sign-in; passkeys enrolled before the field existed read this way until their next use."
                  testId={`passkey-last-used-${passkey.id}`}
                />
              ) : (
                <span data-testid={`passkey-last-used-${passkey.id}`}>
                  Last used {formatTimestamp(passkey.lastUsedAt)}
                </span>
              )}
            </span>
          </span>
          {/*
            ⛔ `tone="default"`, NOT a red tone — this button only ARMS the gate, it deletes nothing.
            The destructive control is the `danger` one below, and a tone that announced consequence
            here would be announcing it about the wrong step.

            ⚠️ THAT IS A HIERARCHY CHOICE, NOT A SAFETY ONE, and the distinction matters enough to
            state: quietening this button does not make removal safer. Removal's safety is the typed
            confirm and nothing else. ⛔ An earlier version of this comment argued the quiet tone
            "stops an operator learning to click through the first step" — that is
            *demote-the-dangerous-thing* wearing different clothes, and it is wrong for the same
            reason `Execute cutover` keeps `primary` (lead ruling, 2026-08-05): **hiding a dangerous
            action in visual noise is not a safety measure, it is just a worse screen.** The claim was
            removed rather than quietly reworded so it cannot be re-derived.

            (It previously hinted red on hover; `Button` owns hover now, and one definition beats the
            hint.)
          */}
          <span className="shrink-0">
            <Button
              tone="default"
              size="sm"
              onClick={() => armRemoval(armed ? null : passkey.id)}
              aria-expanded={armed}
              data-testid={`passkey-remove-${passkey.id}`}
            >
              <Trash2 aria-hidden="true" className="h-3 w-3" />
              {armed ? 'Cancel' : 'Remove'}
            </Button>
          </span>
        </div>

        {armed && (
          <div className="mt-3 border-t border-border-subtle pt-3">
            {/*
              ⛔ THE LAST-CREDENTIAL WARNING IS THE POINT, NOT A CAVEAT. Removing the only passkey is
              the difference between "one fewer way in" and "no way in without the password" — and an
              operator who enrolled a passkey precisely so they would not need the password is exactly
              the person about to do this by accident.
            */}
            {isOnly && (
              /*
                ⛔ THE SENTENCE IS `text-secondary`, NOT `text-state-warning`, AND THAT IS A CONTRAST
                FIX rather than a preference. Light-mode `--color-state-warning` (`rgb(217 119 6)`)
                against `--color-surface-elevated` (`rgb(255 255 255)`) is **3.19:1** — under WCAG AA's
                4.5:1 for body text, and this is `text-xs`. The ICON keeps the warning colour, where
                the 3:1 non-text threshold applies and 3.19:1 passes.

                ⚠️ That ratio is COMPUTED from the two token values via the WCAG relative-luminance
                formula, NOT read off a rendered page — so it is exact for those two colours and says
                nothing about what the `bg-state-warning/10` tint underneath does to the effective
                backdrop. The tint moves it slightly in the safe direction (away from white), so the
                fix holds either way; a browser contrast check is what would pin the tinted number.

                ⚠️ Which is also the right structure anyway: the warning is carried by the icon AND the
                words, so nothing here depends on colour alone.
              */
              <p
                data-testid="passkey-last-warning"
                className="mb-2 flex max-w-[100ch] items-start gap-1.5 rounded-lg border border-state-warning/40 bg-state-warning/10 px-2.5 py-2 text-xs text-secondary"
              >
                <TriangleAlert
                  aria-hidden="true"
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-state-warning"
                />
                <span>
                  This is your only passkey. After removing it you will need your password to sign in.
                </span>
              </p>
            )}
            <label className="flex flex-col gap-1">
              <span className="text-xs text-secondary">
                Type <span className="font-mono font-semibold text-primary">{token}</span> to confirm.
              </span>
              <input
                type="text"
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                onKeyDown={(event) => {
                  // Platform-5. See this file's header for why the guard exists AS WELL AS the
                  // absence of a <form>.
                  if (isForbiddenEnter(event.key)) event.preventDefault();
                }}
                autoComplete="off"
                data-testid="passkey-confirm-input"
                className="w-full rounded-lg border border-border-subtle bg-surface px-3 py-1.5 font-mono text-sm text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
              />
            </label>
            {/*
              ⛔ `tone="danger"` — the one genuinely destructive control here, and now FILLED rather
              than a tinted outline. That is the owner's *"nothing looks like a button… it's too
              subtle"* fix: the previous treatment was `border-state-critical/50 bg-state-critical/10`,
              and a 50%-alpha `rgb(220 38 38)` over white composites to `rgb(238 147 147)` —
              **2.29:1** against the card, against the **3:1** WCAG 1.4.11 asks for a control's visual
              boundary. The 10% fill is weaker still.

              ⚠️ COMPUTED from the token values via the WCAG relative-luminance formula, not read off a
              rendered page — same standing as the warning-text figure above. ⛔ And deliberately NOT
              sourced from `one-button-definition.test.ts`'s inventory statistics: those were
              **withdrawn on 2026-08-05** when its counting regex was found to stop at the first `>`,
              which `onClick={() => …}` contains, so most buttons had been scored as unstyled. Cite the
              tokens, not a count that has been restated once.

              ⚠️ The `mt-2` moved to a wrapper because `Button` deliberately does not forward
              `className` — layout around a button belongs to the parent, a new *variant* belongs in
              `BUTTON_TONE_CLASS`. See `Button.tsx`'s note on why that omission is the point.
            */}
            <div className="mt-2">
              <Button
                tone="danger"
                size="sm"
                onClick={() => void handleRemove(passkey.id)}
                disabled={busy || !matched}
                title={
                  matched ? 'Delete this passkey permanently' : `Type ${token} above to enable this`
                }
                data-testid="passkey-confirm-remove"
              >
                <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                Remove passkey
              </Button>
            </div>
          </div>
        )}
      </li>
    );
  }

  function list(): React.JSX.Element {
    if (passkeys.state.kind === 'loading') {
      /*
        ⛔ SKELETONS SIZED TO THE REAL ROW, not a "Loading…" string: the matching height is what stops
        the dialog resizing under the operator when the answer arrives. The visual blocks are
        `aria-hidden`; the one thing AT is told is the `role="status"` line, because a pulsing
        rectangle announces nothing.
      */
      return (
        <div data-testid="passkey-skeleton" className="flex flex-col gap-2">
          <span
            aria-hidden="true"
            className="h-[3.25rem] rounded-lg border border-border-subtle bg-surface-muted motion-safe:animate-pulse motion-reduce:animate-none"
          />
          <span
            aria-hidden="true"
            className="h-[3.25rem] rounded-lg border border-border-subtle bg-surface-muted motion-safe:animate-pulse motion-reduce:animate-none"
          />
          <span role="status" className="sr-only">
            Reading your passkeys…
          </span>
        </div>
      );
    }
    if (passkeys.state.kind === 'error') {
      /*
        ⛔ ERRORSTATE, never the empty state. "No passkeys registered yet" after a FAILED read tells
        the operator they have none — and the action that follows from that lie is enrolling a
        duplicate, or concluding an existing passkey has been deleted.
      */
      return (
        <ErrorState
          subject="your passkeys"
          message={passkeys.state.message}
          status={passkeys.state.status}
          onRetry={passkeys.reload}
          testId="passkey-errorstate"
        />
      );
    }
    if (passkeys.state.data.length === 0) {
      return (
        <EmptyState
          subject="passkeys enrolled"
          hint="Add one above to sign in with Touch ID, Face ID, a device PIN or a security key."
          testId="passkey-empty"
        />
      );
    }
    const isOnly = passkeys.state.data.length === 1;
    return (
      <ul data-testid="passkey-list" className="flex flex-col gap-2">
        {passkeys.state.data.map((passkey) => row(passkey, isOnly))}
      </ul>
    );
  }

  const blocked = addBlockedReason(deviceName, busy);

  /*
    ⛔ `createPortal` INTO OUR OWN HOST, which the effect above appends to `<body>`. Two things this
    buys, both of which the in-place version could not have:

      · There is a single element meaning "everything else" — every other body child — so `inert` has
        something to mark. In place, this dialog's siblings are the HEADER's children.
      · The dialog escapes any ancestor `overflow`, `transform` or stacking context the chrome grows
        later. `fixed` is defeated by an ancestor transform; a portal is not.

    ⚠️ It stays a React CHILD of `UserMenu` despite living elsewhere in the DOM, so effect ordering is
    unchanged — child effects still run before the parent's, which is what `UserMenu`'s
    focus-suppression depends on. ⛔ Re-checked after the move rather than assumed — `user-menu.test.tsx`'s
    "the Passkeys item … does NOT steal focus from it" passes unchanged, which is the assertion that
    would break if the portal had reordered the effects.
  */
  return createPortal(
    /*
      ⛔ `bg-black/60` is a LITERAL in a package that otherwise uses tokens only, and that is
      deliberate: there is no scrim token, and the plausible substitute — `bg-primary/40` — INVERTS,
      because `--color-primary` is near-white in dark mode, so the scrim would brighten the page
      instead of dimming it. A literal black scrim is correct in both polarities; a token that
      resolves to the wrong end of the ramp is not.

      ⚠️ `z-50` matches the highest z-index in this package (`SectionLayout`'s skip link). Inside the
      portal host — a direct body child — nothing can trap it in a stacking context.
    */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      data-testid="passkey-dialog-overlay"
      /*
        ⛔ Dismiss on the BACKDROP only — `event.target === event.currentTarget`. Without that test any
        click inside the card bubbles here and closes the dialog, which while typing in a text field
        is indistinguishable from the app crashing. `onMouseDown` rather than `onClick` so a drag that
        started inside the card and ended on the backdrop does not dismiss it.
      */
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        data-testid="passkey-dialog"
        /* Programmatically focusable, never a Tab stop of its own. */
        tabIndex={-1}
        onKeyDown={onCardKeyDown}
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-xl flex-col rounded-xl border border-border-subtle bg-surface-elevated p-6 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
      >
        <h2 id={TITLE_ID} className="text-base font-semibold text-primary">
          Passkeys
        </h2>
        <p className="mt-1 max-w-[70ch] text-sm text-secondary">
          Sign in with Touch ID, Face ID, a device PIN or a security key instead of your password.
        </p>

        {/*
          ⛔ THE BODY SCROLLS WHEN THE VIEWPORT IS SHORT, and the title and Done stay put (WS-31b-R17).
          The card is `max-h-[calc(100vh-2rem)]` inside a fixed overlay that cannot scroll. With ten
          recovery codes on show the content is taller than a laptop viewport, and while `holding`
          is true Done, Escape and the backdrop do nothing. Without this region the reveal's ack box
          and Close fall below the viewport, where no mouse can reach them. `min-h-0` lets this flex
          item shrink below its content; `flex-1` gives it the space the header and footer leave.
          `px-1 -mx-1` keeps the focus rings of the controls inside the clip.
        */}
        <div data-testid="passkey-dialog-body" className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
          {/* ── Add ─────────────────────────────────────────────────────────── */}
          <section aria-labelledby="passkey-add-heading" className="mt-5">
            <h3 id="passkey-add-heading" className="text-sm font-semibold text-primary">
              Add a passkey
            </h3>
            <p className="mt-1 max-w-[100ch] text-xs text-muted">
              Name the device first — it is the only thing that tells your passkeys apart later.
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <label className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-xs font-medium text-secondary">Device name</span>
                <input
                  type="text"
                  value={deviceName}
                  onChange={(event) => setDeviceName(event.target.value)}
                  placeholder="MacBook Touch ID"
                  data-testid="passkey-name-input"
                  className="w-full rounded-lg border border-border-subtle bg-surface px-3 py-1.5 text-sm text-primary placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                />
              </label>
              {/*
                ⛔ `tone="primary"` — the dialog's one dominant action (Von Restorff: exactly one per
                view). It was `bg-accent/10`, a 10%-tint outline, which is precisely the class of
                button the owner could not see.
              */}
              <Button
                tone="primary"
                size="md"
                onClick={() => void handleAdd()}
                disabled={!canAdd(deviceName, busy)}
                title={blocked ?? 'Register a new passkey on this device'}
                data-testid="passkey-add"
              >
                {busy ? (
                  <Loader2
                    aria-hidden="true"
                    className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                  />
                ) : (
                  <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                )}
                {busy ? 'Working…' : 'Add passkey'}
              </Button>
            </div>
          </section>

          {/*
            The one error surface for both writes.
            ⛔ `role="alert"` carrying the SERVER's own sentence via `displayMessage` — a generic
            "something went wrong" is the one string guaranteed not to tell the operator what to fix.
          */}
          {error !== null && (
            <p
              role="alert"
              data-testid="passkey-error"
              className="mt-4 rounded-lg border border-state-critical/40 bg-state-critical/5 px-3 py-2 text-xs text-state-critical"
            >
              {error}
            </p>
          )}

          {/* ── The list ────────────────────────────────────────────────────── */}
          <section aria-labelledby="passkey-list-heading" className="mt-6">
            <h3 id="passkey-list-heading" className="text-sm font-semibold text-primary">
              Your passkeys
            </h3>
            {/*
              ⛔ THE LIST scrolls — not the page (cross-cutting rule #1). At a normal height the add
              controls and both headings stay put and only this region moves; the body region above
              scrolls only when the viewport is shorter than the content.
            */}
            <div className="mt-2 max-h-64 overflow-y-auto">{list()}</div>
          </section>

          <RecoveryCodesPanel onRevealChange={setHolding} />
        </div>

        <div className="mt-6 flex justify-end border-t border-border-subtle pt-4">
          <Button
            tone="default"
            size="md"
            onClick={requestClose}
            disabled={holding}
            title={holding ? 'Save the new recovery codes and close them first' : undefined}
            data-testid="passkey-dialog-close"
          >
            Done
          </Button>
        </div>
      </div>
    </div>,
    host,
  );
}
